import { all, put, select } from 'redux-saga/effects';
import _get from 'lodash/get';
import { createDataFromFields, getAppConfig } from '@/common/utils';
import {
  CREATE_SKU_IDENTIFIER_SUCCESS,
  REMOVE_SKU_IDENTIFIER_SUCCESS,
  CREATE_SKU_SUCCESS,
  DELETE_SKU_SUCCESS,
  GET_DETAIL,
  REQUEST_ERROR,
  UPDATE_SKU_FILES_SUCCESS,
  UPDATE_SKU_SUCCESS,
  UPDATE_SKU_IDENTIFIER_SUCCESS,
} from '../types/product';
import {
  deleteVariantFile,
  requestCreateIdentifiers,
  requestCreateProductSku,
  requestDeleteIdentifier,
  requestDeleteSku,
  requestPutIdentifier,
  requestUpdateSku,
  updateVariantFile,
  uploadVariantFile,
} from '../requests/product';
import { onFailRequest, onSuccessRequest } from '@/utils';

/**
 * Delete Sku
 * @param {*} actions
 */
function* deleteSku(actions) {
  try {
    const response = yield requestDeleteSku(actions);
    yield put({
      type: DELETE_SKU_SUCCESS,
      data: response.data,
    });
    const product = yield select((state) => state.product.product);
    yield put({
      type: GET_DETAIL,
      id: product.id,
    });

    yield onSuccessRequest({
      response,
    });
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Handle variant upload file
 * @param {*} productId
 * @param {*} skuId
 * @param {*} files
 * @returns
 */
function handleVariantUploadFiles(productId, skuId, files = []) {
  try {
    const newFiles = [];
    const deleteFiles = [];
    const updateFiles = [];
    const channelData = getAppConfig('channels') ? getAppConfig('channels')[0] : null;
    const channelId = _get(channelData, 'id');
    files.forEach((item, idz) => {
      const {
        id: fileId,
        originFileObj,
        isDeleted = false,
        isNew = false,
        // isUpdated = false,
      } = item;
      if (originFileObj && isNew) {
        // Upload file
        newFiles.push(
          uploadVariantFile({
            productId,
            skuId,
            file: {
              file: originFileObj,
              file_type: 'image',
              file_sub_type: item?.file_sub_type,
              channel: item?.channel,
              display_priority: idz,
              channel_id: channelId,
            },
          }),
        );
      }
      // Call request delete file
      if (isDeleted && fileId) {
        deleteFiles.push(deleteVariantFile({ productId, skuId, fileId }));
      }
      // Call request update file
      if (!isDeleted && !isNew && fileId) {
        const body = {
          channel: item?.channel,
          channel_id: channelId,
          is_active: true,
          _method: 'PUT',
        };
        ['file_type', 'file_sub_type', 'display_priority'].forEach((key) => {
          body[key] = item[key];
        });
        updateFiles.push(
          updateVariantFile({
            productId,
            skuId,
            fileId,
            body,
          }),
        );
      }
    });
    return [...newFiles, ...updateFiles];
  } catch (error) {
    return [];
  }
}

/**
 * Handle variant identifier
 * @param {*} productId
 * @param {*} skuId
 * @param {*} identifiers
 * @returns
 */
function handleVariantIdentifiers(productId, skuId, identifiers = []) {
  const variantIdentifiers = [];
  const variantIdentifiersDel = [];
  identifiers.forEach((el) => {
    const { id, isDeleted } = el;
    if (!id) {
      variantIdentifiers.push(
        requestCreateIdentifiers(productId, skuId, {
          ...createDataFromFields(el, [
            'identifier',
            'integration_id',
            'product_sku_identifier_type_id',
          ]),
          product_sku_id: skuId,
        }),
      );
    }

    if (id && isDeleted) {
      variantIdentifiersDel.push(requestDeleteIdentifier(productId, skuId, id));
    }
  });

  return [...variantIdentifiersDel, ...variantIdentifiers];
}

function* createVariantIdentifier(actions) {
  try {
    const { payload } = actions;
    const { productId, skuId, identifier } = payload;
    const res = yield requestCreateIdentifiers(productId, skuId, identifier);
    yield put({ type: CREATE_SKU_IDENTIFIER_SUCCESS, payload: { skuId, data: res.data } });

    yield onSuccessRequest({ response: res });
    return res.data;
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

function* updateVariantIdentifier(actions) {
  try {
    const { payload } = actions;
    const { productId, skuId, id, data } = payload;
    const res = yield requestPutIdentifier(productId, skuId, id, data);
    yield put({ type: UPDATE_SKU_IDENTIFIER_SUCCESS, payload: { id, skuId } });
    yield onSuccessRequest({ response: res });
    return res.data;
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

function* removeVariantIdentifier(actions) {
  try {
    const { payload } = actions;
    const { productId, skuId, id } = payload;
    const res = yield requestDeleteIdentifier(productId, skuId, id);
    yield put({ type: REMOVE_SKU_IDENTIFIER_SUCCESS, payload: { id, skuId } });
    yield onSuccessRequest({ response: res });
    return res.data;
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

function* createVariantTag(actions) {
  try {
    const { payload } = actions;
    const { productId, skuId, identifier } = payload;
    const res = yield requestCreateIdentifiers(productId, skuId, {
      ...createDataFromFields(identifier, [
        'identifier',
        'integration_id',
        'product_sku_identifier_type_id',
      ]),
      product_sku_id: skuId,
    });
    yield put({ type: CREATE_SKU_IDENTIFIER_SUCCESS, payload: { skuId, data: res } });

    yield onSuccessRequest({ response: res?.data });
    return res;
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

/**
 * Update sku
 * @param {*} actions
 */
function* updateSku(actions) {
  try {
    const { payload } = actions;
    delete payload.files;
    delete payload.identifiers;
    const [res] = yield all([requestUpdateSku(actions)]);
    yield put({ type: UPDATE_SKU_SUCCESS, payload: { skuId: actions.payload.id, data: res.data } });
    yield onSuccessRequest({ response: res });
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

function* updateSkuFiles(actions) {
  try {
    const { productId, id, files } = actions.payload;

    const data = yield all(handleVariantUploadFiles(productId, id, files));
    yield put({
      type: UPDATE_SKU_FILES_SUCCESS,
      payload: { data: data.map((_) => _.data), skuId: id },
    });
    yield onSuccessRequest({});
  } catch (error) {
    yield onFailRequest({
      payload: actions,
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

function* createSku({ payload, callback }) {
  try {
    const { productId, variantData } = payload;
    const res = yield requestCreateProductSku(productId, variantData);
    yield put({ type: CREATE_SKU_SUCCESS });
    yield onSuccessRequest({ response: res });
    callback();
  } catch (error) {
    callback();
    yield onFailRequest({
      error,
      *onCallback() {
        yield put({ type: REQUEST_ERROR });
      },
    });
  }
}

export {
  deleteSku,
  updateSku,
  updateSkuFiles,
  createSku,
  createVariantIdentifier,
  removeVariantIdentifier,
  updateVariantIdentifier,
};
